import React, { useState, useRef, useEffect } from 'react';
import s from './PromtBlocks.module.css';

import { tones, voiceOptions } from './TonesVoices';
import { useAppDispatch } from '../../../../store/store';
import { generateContent, updateProject } from '../../../../shared/api/textProjectApi';
import { useSelector } from 'react-redux';
import {
    selectCurrentProject,
    selectIsLoadingProject,
} from '../../../../store/selectors/projectSelectors';
import { toast } from 'react-toastify';

import Button from '../../../../shared/ui/button/Button';
import IconArrowUp from '../../../../shared/ui/icons/IconArrowUp';
import IconArrowDown from '../../../../shared/ui/icons/IconArrowDown';
import DropDownMenu from '../../../../shared/ui/dropdown-menu/DropDownMenu';

const ToneAndVoiceBlock = () => {
    const [showToneDropdown, setShowToneDropdown] = useState(false);
    const [showVoiceDropdown, setShowVoiceDropdown] = useState(false);

    const [selectedTones, setSelectedTones] = useState<{ value: string; intensity: string }[]>([]);
    const [selectedVoice, setSelectedVoice] = useState<string | undefined>(undefined);

    const [initialTones, setInitialTones] = useState<{ value: string; intensity: string }[]>([]);
    const [initialVoice, setInitialVoice] = useState<string | undefined>(undefined);

    const toneRef = useRef<HTMLDivElement>(null);
    const voiceRef = useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();
    const currentProject = useSelector(selectCurrentProject);
    const loadingProject = useSelector(selectIsLoadingProject);

    const toggleTone = (tag: string) => {
        setSelectedTones((prev) => {
            const exists = prev.find((t) => t.value === tag);
            if (exists) {
                return prev.filter((t) => t.value !== tag);
            } else if (prev.length < 3) {
                return [...prev, { value: tag, intensity: 'neutral' }];
            } else {
                return prev;
            }
        });
    };

    const selectVoice = (tag: string) => {
        setSelectedVoice(tag);
        setShowVoiceDropdown(false);
    };

    useEffect(() => {
        if (currentProject) {
            const projectTones = currentProject.tone || [];
            const projectVoice = currentProject.voice;

            setSelectedTones(projectTones);
            setInitialTones(projectTones);
            setSelectedVoice(projectVoice);
            setInitialVoice(projectVoice);
        }
    }, [currentProject]);

    useEffect(() => {
        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as Node;

            if (toneRef.current && !toneRef.current.contains(target)) {
                setShowToneDropdown(false);
            }

            if (voiceRef.current && !voiceRef.current.contains(target)) {
                setShowVoiceDropdown(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, []);

    const hasChanges = () => {
        const tonesChanged = JSON.stringify(selectedTones) !== JSON.stringify(initialTones);
        const voiceChanged = selectedVoice !== initialVoice;
        return tonesChanged || voiceChanged;
    };

    const handleApply = async () => {
        await dispatch(
            updateProject({ ...currentProject, voice: selectedVoice, tone: selectedTones }),
        );
        // await handleGenerateScript();

        setInitialTones(selectedTones);
        setInitialVoice(selectedVoice);

        console.log(selectVoice, selectedTones);
    };

    const handleGenerateScript = async () => {
        if (currentProject?.id) {
            try {
                const response = await dispatch(
                    generateContent({ projectId: currentProject.id, kind: 'text_only' }),
                ).unwrap();
                console.log('Script generated successfully:', response);
            } catch (error: any) {
                console.error('Failed to handle generate script:', error);
                toast.error(error?.message || 'Failed to handle generate script');
            }
        }
    };

    return (
        <>
            <div className={s.wrapper}>
                {/* TONES */}
                <h3 className={s.main_title}>Text and Voice</h3>
                <div className={s.block} ref={toneRef}>
                    <label className={s.title}>Text tone</label>
                    <p className={s.subtitle}>Overall mood of your post</p>
                    <div className={s.selectWrapper}>
                        <div className={s.selectBox}>
                            {selectedTones.length === 0 ? (
                                <span className={s.placeholder}>Select up to 3</span>
                            ) : (
                                <div className={s.selectedTones}>
                                    {selectedTones.map((toneObj) => {
                                        const tone = tones.find((t) => t.tag === toneObj.value);
                                        return (
                                            <span key={toneObj.value} className={s.tonePill}>
                                                {tone?.text}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <button
                            className={s.dropdownToggle}
                            onClick={() => setShowToneDropdown((prev) => !prev)}
                            aria-label="Toggle tone dropdown"
                            disabled={loadingProject}
                        >
                            {showToneDropdown ? <IconArrowUp /> : <IconArrowDown />}
                        </button>
                    </div>
                    {showToneDropdown && (
                        <div className={`${s.dropdown} ${s.dropdownGrid} ${s.floating}`}>
                            {tones.map((tone) => (
                                <div
                                    key={tone.tag}
                                    className={`${s.dropdownItem} ${selectedTones.find((t) => t.value === tone.tag) ? s.active : ''}`}
                                    onClick={() => toggleTone(tone.tag)}
                                >
                                    {tone.text}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* VOICE */}
                <div className={s.block} ref={voiceRef}>
                    <label className={s.title}>Brand voice</label>
                    <p className={s.subtitle}>Brand’s unique personality</p>
                    <DropDownMenu
                        options={voiceOptions.map((voice) => ({
                            value: voice.tag,
                            disabled: false,
                        }))}
                        selectedOption={selectedVoice || ''}
                        setSelectedOption={setSelectedVoice}
                        placeholder="Select one"
                    />
                </div>

                {hasChanges() && (
                    <Button
                        onClick={handleApply}
                        state={loadingProject ? 'loading' : 'enabled'}
                        text="Apply changes"
                        style={{ background: '#FFDD35', marginTop: '20px' }}
                    />
                )}
            </div>
        </>
    );
};

export default ToneAndVoiceBlock;
